<template>
  <div class="login-content">
    <div class="row g-0">
      <!-- Login Banner -->
      <login-banner></login-banner>
      <!-- /Login Banner -->

      <div class="col-12 col-lg-6 login-wrap-bg">
        <div class="login-wrapper">
          <div class="loginbox">
            <div class="w-100">
              <div
                class="d-flex align-items-center justify-content-between flex-wrap gap-2 login-header"
              >
                <img
                  src="@/assets/logo/sapta.png"
                  class="img-fluid login-brand-logo"
                  alt="Sapta LMS"
                />
                <router-link to="/home" class="link-1 text-nowrap"
                  >Back to Home</router-link
                >
              </div>

              <div
                v-if="snackbar"
                :class="`alert ${snackbarColor} alert-dismissible fade show`"
                role="alert"
              >
                {{ snackbarMessage }}
                <button
                  type="button"
                  class="btn-close custom-btn-close"
                  aria-label="Close"
                  @click="snackbar = false"
                >
                  <i class="isax isax-close-circle5" aria-hidden="true"></i>
                </button>
              </div>

              <div class="topic">
                <h1 class="fs-32 fw-bold mb-2">Email OTP</h1>
                <p class="fs-14 fw-normal mb-0 text-muted">
                  Enter the 4-digit code sent to
                  <span class="fw-medium text-dark">{{ send_to || "your email" }}</span>
                </p>
              </div>

              <form @submit.prevent="submitForm" class="mb-3 pb-3">
                <div class="otp-fieldset mb-4">
                  <label class="form-label d-block text-center mb-3"
                    >Enter OTP code</label
                  >
                  <div
                    class="otp-input-group d-flex justify-content-center align-items-center"
                    role="group"
                    aria-label="OTP digits"
                  >
                    <input
                      ref="otp0"
                      v-model="otpDigits[0]"
                      type="text"
                      inputmode="numeric"
                      pattern="[0-9]*"
                      autocomplete="one-time-code"
                      class="form-control otp"
                      maxlength="1"
                      aria-label="Digit 1 of 4"
                      @input="onOtpInput(0, $event)"
                      @keydown="onOtpKeydown(0, $event)"
                      @paste="onOtpPaste(0, $event)"
                      @focus="onOtpFocus($event)"
                    />
                    <input
                      ref="otp1"
                      v-model="otpDigits[1]"
                      type="text"
                      inputmode="numeric"
                      pattern="[0-9]*"
                      class="form-control otp"
                      maxlength="1"
                      aria-label="Digit 2 of 4"
                      @input="onOtpInput(1, $event)"
                      @keydown="onOtpKeydown(1, $event)"
                      @paste="onOtpPaste(1, $event)"
                      @focus="onOtpFocus($event)"
                    />
                    <input
                      ref="otp2"
                      v-model="otpDigits[2]"
                      type="text"
                      inputmode="numeric"
                      pattern="[0-9]*"
                      class="form-control otp"
                      maxlength="1"
                      aria-label="Digit 3 of 4"
                      @input="onOtpInput(2, $event)"
                      @keydown="onOtpKeydown(2, $event)"
                      @paste="onOtpPaste(2, $event)"
                      @focus="onOtpFocus($event)"
                    />
                    <input
                      ref="otp3"
                      v-model="otpDigits[3]"
                      type="text"
                      inputmode="numeric"
                      pattern="[0-9]*"
                      class="form-control otp"
                      maxlength="1"
                      aria-label="Digit 4 of 4"
                      @input="onOtpInput(3, $event)"
                      @keydown="onOtpKeydown(3, $event)"
                      @paste="onOtpPaste(3, $event)"
                      @focus="onOtpFocus($event)"
                    />
                  </div>
                </div>

                <div class="d-grid">
                  <button
                    class="btn btn-secondary btn-lg"
                    type="submit"
                    :disabled="otp.length < 4 || loading"
                  >
                    Verify &amp; Proceed
                    <i
                      class="isax isax-arrow-right-3 ms-1"
                      aria-hidden="true"
                    ></i>
                  </button>
                </div>
              </form>

              <div
                class="fs-14 fw-normal d-flex align-items-center justify-content-center flex-wrap gap-1"
              >
                <span>Didn’t get the OTP?</span>
                <button
                  type="button"
                  class="btn btn-link link-2 p-0 align-baseline"
                  :disabled="resendDisabled || loading"
                  @click="handleSource('elearning')"
                >
                  Resend OTP
                  <span v-if="resendDisabled">{{ countdownText }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <BaseLoading :dialog="true" v-if="loading"></BaseLoading>
</template>

<script>
import { useAuthStore } from "@/stores/auth-store";
import BaseLoading from "@/components/CustomComponent/shared/Loading.vue";

export default {
  components: {
    BaseLoading,
  },
  data() {
    return {
      loading: false,
      snackbar: false,
      snackbarColor: "",
      snackbarMessage: "",
      send_to: "",
      form: {
        source: "",
      },
      otpDigits: ["", "", "", ""],
      resendDisabled: false,
      resendCountdown: 0,
      resendTimer: null,
    };
  },
  created() {
    this.handleSource("elearning");
  },
  mounted() {
    this.$nextTick(() => {
      const el = this.$refs.otp0;
      if (el && typeof el.focus === "function") el.focus();
    });
  },
  computed: {
    otp() {
      return this.otpDigits.join("");
    },
    countdownText() {
      if (!this.resendDisabled) return "";
      const m = Math.floor(this.resendCountdown / 60)
        .toString()
        .padStart(2, "0");
      const s = (this.resendCountdown % 60).toString().padStart(2, "0");
      return `(${m}:${s})`;
    },
  },
  beforeUnmount() {
    if (this.resendTimer) {
      clearInterval(this.resendTimer);
      this.resendTimer = null;
    }
  },
  methods: {
    focusOtp(index) {
      const el = this.$refs[`otp${index}`];
      if (el && typeof el.focus === "function") el.focus();
    },
    onOtpFocus(e) {
      const el = e?.target;
      if (el && typeof el.select === "function") el.select();
    },
    setOtpAt(index, value) {
      if (index < 0 || index > 3) return;
      this.otpDigits.splice(index, 1, value);
    },
    onOtpInput(index, e) {
      const raw = e?.target?.value ?? "";
      const digits = String(raw).replace(/\D/g, "");
      if (!digits) {
        this.setOtpAt(index, "");
        return;
      }

      const chars = digits.split("");
      for (let i = 0; i < chars.length; i += 1) {
        const targetIndex = index + i;
        if (targetIndex > 3) break;
        this.setOtpAt(targetIndex, chars[i]);
      }

      const focusIndex = Math.min(index + chars.length, 3);
      this.focusOtp(focusIndex);
    },
    onOtpKeydown(index, e) {
      const key = e?.key;
      if (key === "Backspace") {
        if (this.otpDigits[index]) {
          this.setOtpAt(index, "");
          return;
        }
        if (index > 0) {
          this.focusOtp(index - 1);
          this.setOtpAt(index - 1, "");
        }
        return;
      }
      if (key === "ArrowLeft" && index > 0) {
        e.preventDefault();
        this.focusOtp(index - 1);
        return;
      }
      if (key === "ArrowRight" && index < 3) {
        e.preventDefault();
        this.focusOtp(index + 1);
        return;
      }
      if (key && key.length === 1 && !/^\d$/.test(key)) {
        e.preventDefault();
      }
    },
    onOtpPaste(index, e) {
      const text = e?.clipboardData?.getData("text") ?? "";
      const digits = String(text).replace(/\D/g, "");
      if (!digits) return;
      e.preventDefault();
      const chars = digits.split("");
      for (let i = 0; i < chars.length; i += 1) {
        const targetIndex = index + i;
        if (targetIndex > 3) break;
        this.setOtpAt(targetIndex, chars[i]);
      }
      const lastIndex = Math.min(index + chars.length - 1, 3);
      this.focusOtp(lastIndex);
    },
    startResendCountdown() {
      this.resendDisabled = true;
      this.resendCountdown = 60;
      if (this.resendTimer) {
        clearInterval(this.resendTimer);
      }
      this.resendTimer = setInterval(() => {
        if (this.resendCountdown > 0) {
          this.resendCountdown -= 1;
        } else {
          clearInterval(this.resendTimer);
          this.resendTimer = null;
          this.resendDisabled = false;
        }
      }, 1000);
    },
    getErrorMessage(err, source) {
      if (err.response?.data?.error) {
        return err.response.data.error;
      }

      return `Gagal mengirim kode via ${source}`;
    },
    async handleSource(source) {
      this.snackbar = false;
      this.snackbarMessage = "";
      if (this.resendDisabled) return;
      this.startResendCountdown();
      try {
        this.loading = true;
        const response = await useAuthStore().resendCode(
          this.$route.params.key,
          source
        );

        if (!response?.send_to) {
          throw new Error("Tidak dapat mengirim kode verifikasi");
        }

        this.snackbarMessage = "Kode verifikasi telah dikirim ulang";
        this.snackbar = true;
        this.snackbarColor = "alert-success";
        this.send_to = response.send_to;
        this.form.source = source;
        return true;
      } catch (err) {
        const errorMessage = this.getErrorMessage(err, source);
        this.$emit("error", true);
        this.snackbarColor = "alert-danger";
        this.snackbarMessage = errorMessage;
        this.snackbar = true;

        console.error("Failed to resend code:", err);
        return false;
      } finally {
        this.loading = false;
      }
    },

    async submitForm() {
      if (this.otp.length < 4) {
        this.snackbarMessage = "Masukkan 4 digit kode OTP.";
        this.snackbarColor = "alert-danger";
        this.snackbar = true;
        return false;
      }

      this.loading = true;
      try {
        const ok = await useAuthStore().verifyCode(
          this.$route.params.key,
          this.otp
        );

        if (!ok) {
          this.snackbarMessage = "Kode OTP tidak valid.";
          this.snackbarColor = "alert-danger";
          this.snackbar = true;
          return false;
        }
        return true;
      } catch (err) {
        this.snackbarMessage = "Gagal memverifikasi kode.";
        this.snackbarColor = "alert-danger";
        this.snackbar = true;
        return false;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.otp-fieldset {
  border: 0;
  margin: 0 0 1.5rem;
  padding: 0;
  min-width: 0;
  width: 100%;
}
.otp-input-group {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  flex-wrap: nowrap;
  width: 100%;
}
.otp-input-group .form-control.otp {
  width: 64px;
  max-width: 64px;
  min-width: 48px;
  height: 64px;
  min-height: 64px;
  flex: 0 0 64px;
  text-align: center;
  font-size: 24px;
  font-weight: 700;
  border-radius: 12px;
  padding: 0.375rem 0.25rem;
  line-height: 1.2;
  box-sizing: border-box;
}
@media (max-width: 767.98px) {
  .otp-input-group {
    gap: 8px;
  }
  .otp-input-group .form-control.otp {
    width: 52px;
    max-width: 52px;
    height: 52px;
    min-height: 52px;
    flex: 0 0 52px;
    font-size: 20px;
  }
}
</style>
